import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_API = `${BACKEND_URL}/api/analytics`;

const OUTPUT_TYPE_LABELS = {
  text:       'Text',
  table:      'Table',
  bar_chart:  'Bar Chart',
  pie_chart:  'Pie Chart',
  line_chart: 'Line Chart',
};

const COMPLEXITY_COLORS = {
  basic:        'bg-green-100 text-green-800',
  intermediate: 'bg-yellow-100 text-yellow-800',
  advanced:     'bg-red-100 text-red-800',
};

/**
 * GuidedQuestions
 * Displays the top-10 guided questions as clickable cards in the sidebar.
 * Clicking a card executes the query and calls onResult with the response.
 */
const GuidedQuestions = ({ questions, datasetId, mrsId, userId, sessionId, onResult }) => {
  const [loadingId, setLoadingId] = useState(null);
  const [errorId, setErrorId] = useState(null);

  const handleClick = async (question) => {
    if (loadingId) return; // prevent concurrent requests
    const qKey = question._id || question.question;
    setLoadingId(qKey);
    setErrorId(null);
    try {
      // PDF RAG prompts have source='pdf_rag' and no _id / SQL.
      // Route them through the chat endpoint so the coordinator sends them to RAG.
      if (question.source === 'pdf_rag' || !question._id) {
        const res = await axios.post(
          `${ANALYTICS_API}/query/chat?user_id=${userId}`,
          { session_id: sessionId, question: question.question }
        );
        onResult(res.data, question.question);
        return;
      }
      // SQL-based guided question — use the dedicated guided endpoint.
      const questionDatasetId = question.dataset_id || datasetId;
      const res = await axios.post(
        `${ANALYTICS_API}/query/guided?user_id=${userId}`,
        {
          dataset_id:  questionDatasetId,
          mrs_id:      mrsId,
          question_id: question._id,
          session_id:  sessionId || undefined,
        }
      );
      onResult(res.data, question.question);
    } catch (err) {
      setErrorId(qKey);
    } finally {
      setLoadingId(null);
    }
  };

  if (!questions || questions.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic px-1">
        No guided questions available for this dataset.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
        Suggested Questions
      </p>
      {questions.map((q) => {
        return (
          <Card
            key={q._id || q.question}
            onClick={() => handleClick(q)}
            className={`cursor-pointer border transition-all ${
              loadingId === (q._id || q.question)
                ? 'opacity-60 cursor-wait border-blue-300'
                : 'hover:border-blue-400 hover:shadow-sm border-gray-200'
            } ${errorId === (q._id || q.question) ? 'border-red-300' : ''}`}
          >
            <CardContent className="py-3 px-4">
              <p className="text-sm text-gray-800 leading-snug">
                {loadingId === (q._id || q.question) ? '⏳ ' : ''}{q.question}
              </p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Badge variant="outline" className="text-xs">
                  {OUTPUT_TYPE_LABELS[q.output_type] || q.output_type}
                </Badge>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                    COMPLEXITY_COLORS[q.complexity] || 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {q.complexity}
                </span>
              {q.source_file && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium truncate max-w-[140px] ${
                    q.source === 'pdf_rag'
                      ? 'bg-purple-50 text-purple-700'
                      : 'bg-sky-50 text-sky-700'
                  }`} title={q.source_file}>
                    {q.source === 'pdf_rag' ? '📄 ' : ''}{q.source_file}
                  </span>
                )}
              </div>
              {errorId === (q._id || q.question) && (
                <p className="text-xs text-red-500 mt-1">
                  Failed to run. Please try again.
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

GuidedQuestions.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      _id:         PropTypes.string,
      question:    PropTypes.string,
      output_type: PropTypes.string,
      complexity:  PropTypes.string,
      source:      PropTypes.string,
    })
  ).isRequired,
  datasetId:  PropTypes.string.isRequired,
  mrsId:      PropTypes.string.isRequired,
  userId:     PropTypes.string.isRequired,
  sessionId:  PropTypes.string,
  onResult:   PropTypes.func.isRequired,
};

export default GuidedQuestions;
